import React, { useRef, useEffect, useState } from 'react';
import TooltipTriangle from '@assets/svgs/common/tooltip-triangle.svg';
import tw from 'twin.macro';

/**
 * Smart ToolTip component that auto-adjusts position to stay within viewport boundaries.
 *
 * @param {Object} props
 * @param {string} [props.text] - The tooltip text to display.
 * @param {Object} [props.toolTipCustomStyle] - Custom CSS/twin styles for the tooltip.
 * @param {string} [props.placement] - Preferred placement: "top" | "bottom" | "left" | "right". When set, disables auto-adjust.
 * @param {boolean} [props.isOpen] - When true, tooltip is visible (controlled visibility).
 * @param {boolean} [props.showTriangle] - Whether to show the triangle/arrow pointer.
 * @param {boolean} [props.disableSmartPos] - When true, disables auto position adjustment (uses placement or default).
 * @returns {React.ReactElement} The rendered ToolTip component.
 */
const ToolTip = ({
  text,
  toolTipCustomStyle,
  placement,
  isOpen,
  showTriangle,
  disableSmartPos,
}) => {
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState({ horizontal: 'right', vertical: 'center' });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPositioned, setIsPositioned] = useState(false);

  useEffect(() => {
    if (!tooltipRef.current) {
      return;
    }

    const calculatePosition = () => {
      if (placement) {
        let horizontal = 'center';
        let vertical = 'center';

        if (placement === 'top') {
          vertical = 'top';
        } else if (placement === 'bottom') {
          vertical = 'bottom';
        } else if (placement === 'left') {
          horizontal = 'left';
        } else if (placement === 'right') {
          horizontal = 'right';
        }

        setPosition({ horizontal, vertical });
        setIsPositioned(true);
        return;
      }

      const tooltip = tooltipRef.current;
      const tooltipRect = tooltip.getBoundingClientRect();
      const parentRect = tooltip.parentElement.getBoundingClientRect();

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const spacing = 16; // 1rem spacing from boundaries

      let horizontal = 'right';
      let vertical = 'center';
      let offsetX = 0;
      let offsetY = 0;

      // Check horizontal position
      const _rightSpace = viewportWidth - parentRect.right;
      const leftSpace = parentRect.left;

      // If tooltip would overflow on the right
      if (parentRect.right + tooltipRect.width + spacing > viewportWidth) {
        // Try left side
        if (leftSpace > tooltipRect.width + spacing) {
          horizontal = 'left';
        } else {
          // Not enough space on either side, align to right edge with offset
          horizontal = 'right';
          const overflow = parentRect.right + tooltipRect.width + spacing - viewportWidth;
          offsetX = -overflow;
        }
      }

      // Check vertical position
      const _topSpace = parentRect.top;
      const _bottomSpace = viewportHeight - parentRect.bottom;

      // If tooltip would overflow vertically
      if (parentRect.top + tooltipRect.height / 2 > viewportHeight - spacing) {
        vertical = 'top';
      } else if (parentRect.top - tooltipRect.height / 2 < spacing) {
        vertical = 'bottom';
      }

      setPosition({ horizontal, vertical });
      setOffset({ x: offsetX, y: offsetY });
      setIsPositioned(true);
    };

    // Calculate on mount and when hovering
    calculatePosition();

    // Recalculate on window resize
    window.addEventListener('resize', calculatePosition);

    return () => {
      window.removeEventListener('resize', calculatePosition);
    };
  }, [placement]);

  // Dynamic positioning styles
  const getPositionStyles = () => {
    const styles = {};

    // Horizontal positioning
    if (position.horizontal === 'right') {
      styles.left = '100%';
      styles.marginLeft = '16px';
    } else if (position.horizontal === 'left') {
      styles.right = '100%';
      styles.marginRight = '8px';
    } else if (position.horizontal === 'center') {
      styles.left = '50%';
      styles.transform = 'translateX(-50%)';
    }

    // Vertical positioning
    if (position.vertical === 'center') {
      styles.top = '100%';
    } else if (position.vertical === 'top') {
      styles.bottom = '100%';
      styles.marginBottom = '8px';
    } else if (position.vertical === 'bottom') {
      styles.top = '100%';
      styles.marginTop = '8px';
    }

    // Apply offset if needed
    if (offset.x !== 0) {
      styles.transform = `translateX(${offset.x}px)`;
    }

    return styles;
  };

  return (
    <>
      {showTriangle && (
        <img loading="lazy" decoding="async"
          src={TooltipTriangle}
          alt="tooltip-triangle"
          css={[
            tw`absolute top-[calc(100% + 2px)] left-1/2 transform -translate-x-1/2 opacity-0 transition-opacity duration-200 ease-in-out z-30`,
            isOpen && tw`opacity-100`,
          ]}
        />
      )}
      <div
        ref={tooltipRef}
        css={[
          tw`
          absolute
          w-max
          max-w-[250px]
          px-[10px] py-[6px]
          bg-[#2E2E2E]
          text-white
          text-[14px]
          rounded-[5px]
          opacity-0
          pointer-events-auto
          transition-opacity
          duration-200
          ease-in-out
          z-30
          group-hover:opacity-100
          font-medium
          cursor-default
      `,
          !isPositioned && tw`invisible`,
          // If isOpen is undefined, fallback to group hover
          typeof isOpen === 'undefined' && tw`group-hover:opacity-100`,
          // If isOpen is provided, control visibility explicitly
          isOpen && tw`opacity-100`,
          toolTipCustomStyle,
        ]}
        {...(!disableSmartPos && { style: getPositionStyles() })}
      >
        {text}
      </div>
    </>
  );
};

export default ToolTip;
